import type { AlertKind, Alerts } from "@luckymenu/staff-core";

/** How often the alert repeats while something waits. */
const REPEAT_MS = 2500;

/** Two rising notes for a new order, one lower note twice for a call. A stand-in until the owner chooses the sound. */
const TUNES: Record<AlertKind, { frequency: number; at: number }[]> = {
	newOrder: [
		{ frequency: 880, at: 0 },
		{ frequency: 1320, at: 0.18 },
	],
	serviceRequest: [
		{ frequency: 660, at: 0 },
		{ frequency: 660, at: 0.25 },
	],
};

/**
 * Alerts on the web: a short Web Audio tune repeated every 2.5 seconds until
 * staff-core says stop, and a vibration where the device has one.
 *
 * Browsers start audio only after the person has clicked or typed on the
 * page. The first click or key press anywhere turns sound on (the app also
 * asks for one at sign-in: `sound.allow()` from its button). Until then the
 * banner still shows.
 */
export function webAlerts(): Alerts {
	let timer: ReturnType<typeof setInterval> | null = null;
	let audio: AudioContext | null = null;
	const listeners = new Set<() => void>();
	const notify = () => {
		for (const listener of listeners) listener();
	};

	const context = (): AudioContext | null => {
		if (audio) return audio;
		const Ctor = globalThis.AudioContext;
		if (!Ctor) return null;
		audio = new Ctor();
		audio.onstatechange = notify;
		return audio;
	};

	const allowed = () => audio?.state === "running";

	const allow = async (): Promise<boolean> => {
		const ctx = context();
		if (!ctx) return false;
		try {
			await ctx.resume();
		} catch {
			// Still blocked: the next click tries again.
		}
		notify();
		return allowed();
	};

	// Any click or key press on the page is the gesture the browser wants. On
	// `click`, not `pointerdown`: the sound request disappears once sound is on,
	// and must not move the page between pressing and releasing.
	const onGesture = () => {
		void allow().then((ok) => {
			if (!ok) return;
			document.removeEventListener("click", onGesture, true);
			document.removeEventListener("keydown", onGesture, true);
		});
	};
	document.addEventListener("click", onGesture, true);
	document.addEventListener("keydown", onGesture, true);
	// After a click earlier on this page (such as signing in with an email code), no new gesture is needed.
	if ((navigator as { userActivation?: { hasBeenActive: boolean } }).userActivation?.hasBeenActive)
		void allow();

	const play = (kind: AlertKind) => {
		const ctx = audio;
		if (ctx?.state === "running") {
			for (const note of TUNES[kind]) {
				const start = ctx.currentTime + note.at;
				const osc = ctx.createOscillator();
				const gain = ctx.createGain();
				osc.type = "sine";
				osc.frequency.value = note.frequency;
				gain.gain.setValueAtTime(0.0001, start);
				gain.gain.exponentialRampToValueAtTime(0.25, start + 0.02);
				gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
				osc.connect(gain).connect(ctx.destination);
				osc.start(start);
				osc.stop(start + 0.4);
			}
		}
		try {
			navigator.vibrate?.(200);
		} catch {
			// Not allowed before a gesture either.
		}
	};

	return {
		start(kind) {
			if (timer) clearInterval(timer);
			play(kind);
			timer = setInterval(() => play(kind), REPEAT_MS);
		},
		stop() {
			if (timer) clearInterval(timer);
			timer = null;
		},
		sound: {
			allowed,
			allow,
			subscribe(listener) {
				listeners.add(listener);
				return () => listeners.delete(listener);
			},
		},
	};
}
